import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import {
  WaveSine, House, ChartLineUp, Sparkle, CalendarBlank, Megaphone, Storefront,
  Tray, Coins, SignOut, PlugsConnected, MusicNotes, ChatCircleDots,
  Buildings, PaintBrush, List, X,
} from '@phosphor-icons/react';
import { Toaster } from '@/components/ui/sonner';

const formatCredits = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  const number = Number(value);

  if (!Number.isFinite(number)) {
    return '—';
  }

  return number.toFixed(1);
};

// Mirrors the 5 entry points on the landing page, so the sidebar structure matches
// what a new user already saw before signing up.
const groups = [
  {
    label: 'Dashboard',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: ChartLineUp, testid: 'nav-dashboard' },
    ],
  },
  {
    label: 'AI Social Media Posting',
    items: [
      { to: '/create', label: 'Create & Post', icon: Sparkle, testid: 'nav-create' },
      { to: '/scheduler', label: 'Scheduler', icon: CalendarBlank, testid: 'nav-scheduler' },
      { to: '/publishing-history', label: 'Publishing History', icon: List, testid: 'nav-publishing-history' },
    ],
  },
  {
    label: 'Ads',
    items: [
      { to: '/creative', label: 'Ad Creative', icon: PaintBrush, testid: 'nav-creative' },
      { to: '/ads', label: 'Ad Campaigns', icon: Megaphone, testid: 'nav-ads' },
    ],
  },
  {
    label: 'Curators',
    items: [
      { to: '/marketplace', label: 'Curators', icon: Storefront, testid: 'nav-marketplace' },
      { to: '/inbox', label: 'Curator Inbox', icon: Tray, testid: 'nav-inbox' },
      { to: '/curator', label: 'Become a curator', icon: MusicNotes, testid: 'nav-curator' },
    ],
  },
  {
    label: 'Labels',
    items: [
      { to: '/labels', label: 'Labels (Demos)', icon: Buildings, testid: 'nav-labels' },
      { to: '/label-portal', label: 'Label Portal', icon: Tray, testid: 'nav-label-portal' },
    ],
  },
  {
    label: 'Account',
    items: [
      { to: '/profile', label: 'Profile', icon: Buildings, testid: 'nav-profile' },
      { to: '/connections', label: 'Connections', icon: PlugsConnected, testid: 'nav-connections' },
      { to: '/credits', label: 'Credits & Plans', icon: Coins, testid: 'nav-credits' },
    ],
  },
];

const chatItem = { to: '/chat', label: 'AI Chat', icon: ChatCircleDots, testid: 'nav-chat' };

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );

  const toggleSidebar = () => setCollapsed(v => !v);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex overflow-x-hidden">
      {/* Mobile backdrop when sidebar is expanded */}
      {!collapsed && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setCollapsed(true)}
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
        />
      )}

      <aside
        className={[
          "border-r border-zinc-900 bg-zinc-950/95 backdrop-blur-xl",
          "flex flex-col shrink-0 transition-[width,transform] duration-200 ease-out",
          "fixed inset-y-0 left-0 z-50",
          "md:sticky md:top-0 md:h-screen md:z-30",
          collapsed ? "w-16" : "w-64",
        ].join(" ")}
      >
        {/* Header */}
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} px-2 py-5`}>
          <NavLink
            to="/dashboard"
            className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2'} min-w-0`}
            title="TuneAmplifier"
          >
            <WaveSine size={22} weight="duotone" className="text-[#ccff00] shrink-0" />
            {!collapsed && (
              <span className="font-display font-bold tracking-tight text-lg truncate">
                TUNEAMPLIFIER
              </span>
            )}
          </NavLink>

          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            className={`${
              collapsed ? 'absolute top-4 left-1/2 -translate-x-1/2' : ''
            } w-9 h-9 rounded-lg border border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 flex items-center justify-center transition-colors`}
          >
            {collapsed ? <List size={18} /> : <X size={18} />}
          </button>
        </div>

        {/* Credit chip */}
        <NavLink
          to="/credits"
          title={collapsed ? `Credits: ${formatCredits(user?.credits)}` : undefined}
          className={`${
            collapsed
              ? 'mx-2 mb-5 justify-center px-0'
              : 'mx-2 mb-6 px-3'
          } rounded-lg border border-zinc-800 bg-zinc-900/40 py-2 flex items-center hover:border-[#ccff00]/40 transition-[border-color]`}
          data-testid="nav-credit-chip"
        >
          {collapsed ? (
            <Coins size={20} weight="duotone" className="text-[#ccff00]/70" />
          ) : (
            <>
              <div>
                <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">
                  Credits
                </div>
                <div className="font-display text-lg text-[#ccff00]">
                  {formatCredits(user?.credits)}
                </div>
              </div>
              <Coins size={20} weight="duotone" className="text-[#ccff00]/60 ml-auto" />
            </>
          )}
        </NavLink>

        <div className="px-1 mb-3">
          <NavLink
            to="/?public=1"
            data-testid="nav-home"
            title={collapsed ? "Home" : undefined}
            className={({ isActive }) =>
              `flex items-center ${
                collapsed ? 'justify-center' : 'gap-3'
              } px-3 py-2 rounded-lg text-sm font-medium transition-[background-color,color] ${
                isActive
                  ? 'bg-zinc-900 text-[#ccff00] border border-zinc-800'
                  : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
              }`
            }
          >
            <House size={18} weight="duotone" />
            {!collapsed && <span>Home</span>}
          </NavLink>
        </div>

        <nav className="flex-1 flex flex-col gap-4 overflow-y-auto px-1">
          {groups.map(g => (
            <div key={g.label}>
              {!collapsed && (
                <div className="px-3 mb-1 font-mono text-[10px] uppercase tracking-wider text-zinc-600">
                  {g.label}
                </div>
              )}

              <div className="flex flex-col gap-0.5">
                {g.items.map(({ to, label, icon: Icon, testid }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={testid}
                    title={collapsed ? label : undefined}
                    className={({ isActive }) =>
                      `flex items-center ${
                        collapsed ? 'justify-center' : 'gap-3'
                      } px-3 py-2 rounded-lg text-sm font-medium transition-[background-color,color] ${
                        isActive
                          ? 'bg-zinc-900 text-[#ccff00] border border-zinc-800'
                          : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
                      }`
                    }
                  >
                    <Icon size={18} weight="duotone" />
                    {!collapsed && <span>{label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}

          <div className="border-t border-zinc-900 pt-3 mt-1">
            <NavLink
              to={chatItem.to}
              data-testid={chatItem.testid}
              title={collapsed ? chatItem.label : undefined}
              className={({ isActive }) =>
                `flex items-center ${
                  collapsed ? 'justify-center' : 'gap-3'
                } px-3 py-2 rounded-lg text-sm font-medium transition-[background-color,color] ${
                  isActive
                    ? 'bg-zinc-900 text-[#ccff00] border border-zinc-800'
                    : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60'
                }`
              }
            >
              <chatItem.icon size={18} weight="duotone" />
              {!collapsed && <span>{chatItem.label}</span>}
            </NavLink>
          </div>
        </nav>

        {/* User footer */}
        <div className="border-t border-zinc-900 pt-4 mt-4 px-2 pb-4">
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className={`w-full flex items-center ${
              collapsed ? 'justify-center' : 'gap-3'
            } px-1 mb-3 text-left`}
            title={collapsed ? 'Profile' : undefined}
          >
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="w-8 h-8 rounded-full border border-zinc-800 shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-zinc-800 shrink-0" />
            )}

            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="text-xs font-mono text-zinc-500 truncate">
                  {user?.email}
                </div>
                <div className="text-xs font-display truncate">
                  {user?.name}
                </div>
              </div>
            )}
          </button>

          <button
            data-testid="logout-btn"
            onClick={async () => {
              await logout();
              navigate('/');
            }}
            title={collapsed ? "Sign out" : undefined}
            className={`w-full flex items-center ${
              collapsed ? 'justify-center' : 'gap-2'
            } px-3 py-2 text-sm text-zinc-400 hover:text-zinc-100 rounded-lg hover:bg-zinc-900/60 transition-colors`}
          >
            <SignOut size={18} />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-h-screen min-w-0 overflow-x-hidden pl-16 md:pl-0">
        <div className="w-full max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>

      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}
